package com.klantenstop.web.components.form

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.klantenstop.web.utils.Constants.PASSWORD_MIN_LENGTH
import com.klantenstop.web.utils.MVI
import com.materialkobweb.utils.ViewModel
import com.materialkobweb.components.widgets.DsEditableField
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.silk.components.text.SpanText
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.styles.withAlpha
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px

data class RegisterFormData(
    val firstName: String,
    val lastName: String,
    val emailAddress: String,
    val password: String,
) {
    val isValid: Boolean
        get() = firstName.isNotBlank() &&
                lastName.isNotBlank() &&
                emailAddress.isNotBlank() &&
                password.length >= PASSWORD_MIN_LENGTH
}

@Composable
fun RegisterForm(
    validateFields: Boolean = false,
    onFormDataChanged: (RegisterFormData) -> Unit,
    onCommit: () -> Unit
) {
    val viewModel = remember { RegisterFormViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is RegisterFormContract.Effect.OnCommit -> onCommit()
            }
        }
    }

    LaunchedEffect(validateFields) {
        if (!validateFields) return@LaunchedEffect

        viewModel.sendIntent(RegisterFormContract.Intent.EnableValidation)
    }

    LaunchedEffect(state) {
        onFormDataChanged(
            RegisterFormData(
                firstName = state.firstName,
                lastName = state.lastName,
                emailAddress = state.emailAddress,
                password = state.password
            )
        )
    }

    RegisterFormContent(
        state = state,
        sendIntent = viewModel::sendIntent
    )
}

@Composable
private fun RegisterFormContent(
    state: RegisterFormContract.State,
    sendIntent: (RegisterFormContract.Intent) -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.px, Alignment.CenterVertically)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.px)
        ) {
            DsEditableField(
                modifier = Modifier.fillMaxWidth(),
                id = "inputFirstName",
                label = "Voornaam",
                placeholder = "Voornaam",
                value = state.firstName,
                valid = !state.showValidation || state.firstName.isNotEmpty(),
                onValueChange = { sendIntent(RegisterFormContract.Intent.UpdateFirstName(it)) },
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )

            DsEditableField(
                modifier = Modifier.fillMaxWidth(),
                id = "inputLastName",
                label = "Achternaam",
                placeholder = "Achternaam",
                value = state.lastName,
                valid = !state.showValidation || state.lastName.isNotEmpty(),
                onValueChange = { sendIntent(RegisterFormContract.Intent.UpdateLastName(it)) },
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )

        }

        DsEditableField(
            modifier = Modifier.fillMaxWidth(),
            id = "inputEmail",
            label = "E-mailadres",
            placeholder = "E-mailadres",
            value = state.emailAddress,
            valid = !state.showValidation || state.emailAddress.isNotEmpty(),
            onValueChange = { sendIntent(RegisterFormContract.Intent.UpdateEmail(it)) },
            required = true,
            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
        )

        Column(
            modifier = Modifier.fillMaxWidth()
        ) {
            DsEditableField(
                modifier = Modifier.fillMaxWidth(),
                id = "inputPassword",
                label = "Wachtwoord",
                placeholder = "Wachtwoord",
                value = state.password,
                valid = !state.showValidation || state.password.length >= PASSWORD_MIN_LENGTH,
                onValueChange = { sendIntent(RegisterFormContract.Intent.UpdatePassword(it)) },
                type = InputType.Password,
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            ) { sendIntent(RegisterFormContract.Intent.OnCommit) }

            SpanText(
                text = "Minimum 8 tekens waarvan minstens 1 hoofdletter, 1 cijfer en 1 symbool",
                modifier = Modifier
                    .margin(left = 8.px)
                    .fontSize(12.px)
                    .lineHeight(14.px)
                    .color(MaterialColorVars.Outline.value())
            )
        }
    }
}

class RegisterFormViewModel : ViewModel(), RegisterFormContract {

    private val _state = MutableStateFlow(RegisterFormContract.State())
    override val state = _state.asStateFlow()

    private val _effect = MutableSharedFlow<RegisterFormContract.Effect>()
    override val effect = _effect.asSharedFlow()

    override fun sendIntent(intent: RegisterFormContract.Intent) = coroutineScope.launch {
        when (intent) {
            is RegisterFormContract.Intent.UpdateFirstName -> updateState { it.copy(firstName = intent.value) }
            is RegisterFormContract.Intent.UpdateLastName -> updateState { it.copy(lastName = intent.value) }
            is RegisterFormContract.Intent.UpdateEmail -> updateState { it.copy(emailAddress = intent.value) }
            is RegisterFormContract.Intent.UpdatePassword -> updateState { it.copy(password = intent.value) }
            is RegisterFormContract.Intent.EnableValidation -> updateState { it.copy(showValidation = true) }
            is RegisterFormContract.Intent.OnCommit -> emitEffect(RegisterFormContract.Effect.OnCommit)
        }
    }

    override fun emitEffect(effect: RegisterFormContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (RegisterFormContract.State) -> RegisterFormContract.State) {
        _state.update(block)
    }
}


interface RegisterFormContract : MVI<RegisterFormContract.State, RegisterFormContract.Intent, RegisterFormContract.Effect> {
    data class State(
        val firstName: String = "",
        val lastName: String = "",
        val emailAddress: String = "",
        val password: String = "",
        val showValidation: Boolean = false,
    )

    sealed class Intent {
        data object OnCommit : Intent()
        data object EnableValidation : Intent()
        data class UpdateFirstName(val value: String) : Intent()
        data class UpdateLastName(val value: String) : Intent()
        data class UpdateEmail(val value: String) : Intent()
        data class UpdatePassword(val value: String) : Intent()
    }

    sealed class Effect {
        data object OnCommit : Effect() // TODO: probably send the user data along!!
    }
}
